/**
 * Feature 099 - Checkr Background Check Integration (Vetting)
 * Real API client — replaces mocks/vetting.store.ts. Maps 1:1 onto
 * backend/src/features/vetting/controllers/admin-vetting.controller.ts.
 */

import { apiClient } from '@/core/api/client';
import type { ProviderVettingSummary, VettingCheckSnapshot } from '../types/vetting.types';

export async function getProviderVettingStatus(providerId: string): Promise<ProviderVettingSummary> {
  const response = await apiClient.get<ProviderVettingSummary>(
    `/admin/providers/${providerId}/vetting-status`,
  );
  return response.data;
}

/**
 * Batched version — one request for N providers instead of N requests. Fixes
 * the Active Providers table's N+1 (one useProviderVettingStatus call per row
 * across 300+ providers was flooding the network tab and hitting connection
 * limits).
 */
export async function getProviderVettingStatusBatch(
  providerIds: string[],
): Promise<Record<string, ProviderVettingSummary>> {
  if (providerIds.length === 0) return {};
  const response = await apiClient.post<Record<string, ProviderVettingSummary>>(
    '/admin/providers/vetting-status/batch',
    { providerIds },
  );
  return response.data;
}

export async function overrideCheck(checkId: string, note: string): Promise<VettingCheckSnapshot> {
  const response = await apiClient.post<VettingCheckSnapshot>(
    `/admin/vetting-checks/${checkId}/override`,
    { note },
  );
  return response.data;
}

export async function declineCheck(checkId: string, note?: string): Promise<VettingCheckSnapshot> {
  const response = await apiClient.post<VettingCheckSnapshot>(
    `/admin/vetting-checks/${checkId}/decline`,
    { note },
  );
  return response.data;
}

export async function restartCheck(checkId: string): Promise<VettingCheckSnapshot> {
  const response = await apiClient.post<VettingCheckSnapshot>(`/admin/vetting-checks/${checkId}/restart`);
  return response.data;
}

export async function startCheck(providerId: string): Promise<{ providerId: string; started: boolean }> {
  const response = await apiClient.post<{ providerId: string; started: boolean }>(
    `/admin/providers/${providerId}/vetting-checks/start`,
  );
  return response.data;
}
